import { useState, useEffect, useCallback, useRef } from "react";
import { createVerificationSession, getVerificationStatus, ApiError } from "../../shared/api";
import {
  addAPIBreadcrumb,
  addVerificationFlowBreadcrumb,
} from "../../shared/utils/sentryBreadcrumbs";
import {
  setVerificationUser,
  setVerificationAttemptId,
  trackFlowError,
  trackIProovReloadRequest,
  trackSupportCheck,
  flushSentry,
} from "../../shared/utils/sentryTracking";
import { checkIProovSupport } from "../../shared/utils/iproovSupport";
import { loadIProovWebSdk, IProovSdkLoadError, reloadOnceForIProovSdk, clearIProovSdkReload } from "../../shared/api/vendors/iproov";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { resolveParentOrigin } from "../../shared/utils/parentOrigin";
import { isTokenExpiringSoon } from "../../shared/utils/tokenUtils";
import { API_CONFIG, LIVENESS_PREFLIGHT } from "../../shared/constants/config";
import { logger } from "../../shared/utils/logger";
import { setAnalyticsIdentity, setAnalyticsEnabled, setAnalyticsTraits, resolveReferenceType, type ParentService } from "../../shared/analytics/analyticsClient";
import { trackViewed, trackClicked, trackOutcomePassed, trackOutcomeFailed } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS, type OutcomeSection } from "../../shared/analytics/documentCaptureEvents";
import type {
  FaceVerificationSuccessPayload,
  FaceVerificationErrorPayload,
  VerificationCompleteMessage,
  VerificationErrorMessage,
  VerificationStepMessage,
  VerificationReloadMessage,
  VerificationAttemptFailedMessage,
  VerificationStep,
} from "../../shared/types";

// "permission_blocked" — camera or device-motion permission was denied. We keep
// the iframe alive and show a recovery screen with a Reload button (host-page
// reload re-prompts on iOS Safari) instead of hard-erroring to the parent.
export type FaceVerificationState = "initializing" | "ready" | "capturing" | "processing" | "completed" | "error" | "permission_blocked" | "capture_failed";

/** Which permission the user is recovering from on the "permission_blocked" screen. */
export type BlockedPermission = "camera" | "motion";

/** What the caller knows about a terminal error, beyond what the parent is told. */
export interface VerificationFailureContext {
  /**
   * The member finished the scan and iProov returned a verdict on it. Only iProov's
   * `failed` handler can know this — the code list separating a verdict from an
   * infrastructure fault is scoped to that one event. It gates the processing-screen view
   * and the stage move, so a rejected scan traverses the funnel like a clean one; it does
   * NOT change the outcome, which is `failed` either way.
   */
  scanCompleted?: boolean;
}

/**
 * Backend verdicts that mean the attempt did not pass. Anything else — `pending`, absent, or a
 * value we don't recognise — stays a pass: we resolve on the first 200 and never re-poll, so a
 * non-terminal answer is not a rejection.
 *
 * The `reason` filed is the backend's own `outcome` string, verbatim — no vocabulary of ours in
 * between, so a verdict reads the same in analytics as in the backend record. Note this differs
 * from SSN Card, which collapses all three onto `verification_rejected`: a cross-flow rejection
 * query has to know both sets.
 */
const BACKEND_REJECTION_OUTCOMES = new Set(["failed", "fraudulent", "abandoned"]);

/**
 * Custom hook to manage face verification flow
 *
 * Handles:
 * - Receiving initialization data via postMessage from parent window
 * - Creating verification session with backend
 * - Polling for verification status
 * - Sending results to parent window via postMessage
 */
export function useFaceVerification() {
  const [state, setState] = useState<FaceVerificationState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [iproovToken, setIproovToken] = useState<string | null>(null);
  const [iproovBaseUrl, setIproovBaseUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState({ percent: 0, message: "" });
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  const [consentEnabled, setConsentEnabled] = useState<boolean>(false);
  // Which permission the recovery ("permission_blocked") screen is for; drives the
  // reload metric's permission-type tag and lets the UI show the right copy.
  const [blockedPermission, setBlockedPermission] = useState<BlockedPermission | null>(null);
  // null = waiting on the host's IDME_RETRY_DECISION, so the retry button stays pending.
  // The budget is server-side (live_selfie_attempts) and this frame cannot see it.
  const [retryAllowed, setRetryAllowed] = useState<boolean | null>(null);
  // Stored as a ref — expiry is checked imperatively before API calls, not used for rendering
  const tokenExpiresAtRef = useRef<number | null>(null);
  // Last capture failure, so the fall-over path can report the real reason rather than a
  // generic one if the host never answers.
  const captureFailureRef = useRef<{ feedbacks: string[]; message: string } | null>(null);

  // Where the journey currently is, and so which `section` its one Client Outcome files
  // under. A ref, not state: outcomes are filed synchronously from event handlers, often in
  // the same tick as the transition that moved the flow, and a state update wouldn't have
  // landed yet. Starts at `init` — a failure before anything renders is a session fault,
  // which is exactly what `init` means, so there's no null case to handle.
  const flowSectionRef = useRef<OutcomeSection>(DOCUMENT_CAPTURE_SECTIONS.INIT);

  /**
   * Advance the flow: the analytics section always, the view state when the two move
   * together. They often don't — the permission gate opens before any state change, and
   * `ready` lands before iProov renders the info screen — so `next` is optional.
   */
  const setFlowStage = useCallback((section: OutcomeSection, next?: FaceVerificationState) => {
    flowSectionRef.current = section;
    if (next) setState(next);
  }, []);

  /**
   * The one filer for a failing journey's Client Outcome, because at most one Outcome may
   * fire per launch (see documentCaptureEvents) and there are now two terminal paths that
   * want to file one: the error path, and a host refusal that deliberately never reaches
   * it (VEX-7653). Whichever concludes the journey first wins, so a stray late event —
   * iProov can still emit while we sit on the refused `failed` slot waiting for the host
   * to take over — cannot file a second.
   */
  const outcomeFiledRef = useRef(false);
  const fileFailureOutcome = useCallback((reason: string) => {
    if (outcomeFiledRef.current) return;
    outcomeFiledRef.current = true;
    trackOutcomeFailed(flowSectionRef.current, reason);
  }, []);

  // Set user context in Sentry when verification ID is available
  useEffect(() => {
    if (verificationId) {
      setVerificationUser(verificationId, sessionToken?.substring(0, 8));
    }
  }, [verificationId, sessionToken]);

  // Track state transitions as breadcrumbs
  useEffect(() => {
    addVerificationFlowBreadcrumb(state, verificationId, { progress: progress.percent });
  }, [state, verificationId, progress.percent]);

  const initializeFaceVerification = () => {
    try {
      // Resolve the parent origin robustly. A plain document.referrer breaks after
      // a host-page reload (camera/motion permission recovery): the reloaded
      // iframe's referrer can become its OWN origin, so IDME_READY would target
      // ourselves, the parent never replies with IDME_INIT, and the flow hangs.
      // resolveParentOrigin() prefers ancestorOrigins / a persisted value, is
      // allowlist-checked, and never returns self. (Mirrors useLegalIdVerification.)
      const origin = resolveParentOrigin();
      if (!origin) {
        throw new Error("Cannot determine a valid parent origin (referrer/ancestorOrigins/stored all unavailable or not allowlisted)");
      }

      logger.log("[FaceVerification] Parent origin validated:", origin);
      setParentOrigin(origin);

      // Listen for initialization data from parent window via postMessage
      const handleInitMessage = async (event: MessageEvent) => {
        // Validate message type
        if (!event.data || event.data.type !== "IDME_INIT") {
          return;
        }

        logger.log("[FaceVerification] Received initialization data via postMessage");

        const { sessionToken: token, tokenExpiresAt, externalReferenceId, attemptId, parentService, purpose, consent_enabled, analyticsEnabled } = event.data.payload;
        setConsentEnabled(consent_enabled === true);
        // Analytics: host rollout switch, set before the first tracked event. Strict `=== true`
        // so a missing or non-boolean field stays off.
        setAnalyticsEnabled(analyticsEnabled === true);
        // Analytics: why this check is running (VEX-7807), tagging every event of the launch
        // so PAD-gate liveness is separable from ordinary IAL2 liveness. Validated, not cast:
        // same untyped postMessage boundary as parentService below, and startFaceLiveness is
        // customer-embeddable, so an unvalidated value would be an unbounded warehouse key.
        // Skipped entirely when absent — a { purpose: undefined } entry would leave traits
        // non-empty and ship `traits: {}`, which reads as data.
        if (purpose === "ial2" || purpose === "pad_in_tr") {
          setAnalyticsTraits({ purpose });
        } else if (purpose) {
          logger.warn(`[FaceVerification] ignoring unrecognised analytics purpose "${purpose}"`);
        }
        if (typeof tokenExpiresAt === "number") {
          tokenExpiresAtRef.current = tokenExpiresAt;
        }

        // Analytics/Sentry: attemptId (IVA attempt.uuid) if the host sent it, else
        // externalReferenceId, else synthetic. Separate from its iProov user_id role below.
        const attemptTag = attemptId ?? externalReferenceId ?? `web_${Date.now()}`;

        // Tagged before session creation so even a creation failure is joinable to the attempt.
        // Stable across permission-recovery reloads, unlike verificationId.
        setVerificationAttemptId(attemptTag);

        // Remove listener after receiving data
        window.removeEventListener("message", handleInitMessage);

        try {
          // Bind analytics identity (phase 2 of init)
          // Analytics: validated, not cast — same untyped postMessage boundary as
          // analyticsEnabled, so an unexpected string can't reach context.app.parent.
          const resolvedParentService: ParentService = parentService === "iva" || parentService === "ipa" ? parentService : "iva";
          setAnalyticsIdentity({
            parentService: resolvedParentService,
            referenceType: resolveReferenceType(resolvedParentService),
            referenceId: attemptTag,
          });
          // Analytics: verification_session_started marker view (top of funnel). The
          // section stays `init` (its starting value) until iProov renders something —
          // a pre-render failure is a session fault and files there.
          trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.SESSION_STARTED, "section", { section: DOCUMENT_CAPTURE_SECTIONS.INIT });

          // Guard: abort if the token won't last another 2 minutes
          if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
            logger.warn("[FaceVerification] Token expiring soon, aborting session creation");
            // Analytics: session can't start — record the session-stage failure.
            fileFailureOutcome("token_expired");
            setState("error");
            setError("Session token has expired");
            // Terminal, and posted directly rather than via sendErrorToParent (parentOrigin
            // is still stale here), so latch the one-result invariant by hand.
            resultReportedRef.current = true;
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_ERROR",
              payload: { errorCode: "token_expired", message: "Session token has expired" },
            } as VerificationErrorMessage, origin);
            return;
            // sessionToken is never set — consent GET useEffect won't trigger
          }

          setSessionToken(token);

          // Pre-flight device check before minting a session (VEX-7617). Prompt-free, so it
          // costs the member nothing; fails open, so only a decisive negative ever stops us.
          const support = await checkIProovSupport();

          if (!support.ran || !support.supported) {
            trackSupportCheck(null, token, support);
          }

          if (support.ran && !support.supported) {
            // Emitted regardless of ENFORCE: in report-only this is the only record that an
            // attempt WOULD have been blocked, which is what sizes the false-positive rate.
            trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.PREFLIGHT_UNSUPPORTED, "indicator", {
              section: flowSectionRef.current,
              reason: support.failedTests[0] ?? "unknown",
            });
          }

          // `some`, not `every`: localStorage failing is ~96% terminal whatever else failed
          // alongside it, so an untrusted test can't veto a trusted one (VEX-7881).
          const blocking = support.failedTests.filter((t) =>
            (LIVENESS_PREFLIGHT.ENFORCEABLE_TESTS as readonly string[]).includes(t)
          );

          if (support.ran && !support.supported && blocking.length > 0) {
            const primary = blocking[0] ?? "unknown";
            logger.warn(`[FaceVerification] Pre-flight check failed (${primary}) — not creating a session`);

            // Only when we actually block — in report-only the member proceeds and files a
            // real outcome later. Prefixed so the funnel can tell ours from iProov's codes.
            trackOutcomeFailed(flowSectionRef.current, `preflight_${primary}`);

            // `origin`, not sendStepToParent — that closes over parentOrigin state, still
            // null here. Same reason the session-creation catch below does this.
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_STEP",
              payload: { step: "device_unsupported" },
            } as VerificationStepMessage, origin);

            setState("error");
            setError("Device or browser not supported");

            // Same shape handleUnsupported posts post-mount, so IVA needs no special case.
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_ERROR",
              payload: {
                errorCode: "face_liveness_failed",
                message: "Device or browser not supported",
                feedback: "error_not_supported",
              },
            } as VerificationErrorMessage, origin);
            return;
          }

          // Create verification session with backend
          // This calls POST /v1/verifications/faces with providers: ["IPROOV"]
          logger.log("[FaceVerification] Creating verification session...");

          addAPIBreadcrumb('POST /v1/verifications/faces', 'pending', { flow: 'face_liveness' });

          const session = await createVerificationSession({
            sessionToken: token,
            flow: "face_liveness",
            externalReferenceId: externalReferenceId || undefined,
          });

          logger.log("[FaceVerification] Session created:", {
            verificationId: session.verificationId,
            hasToken: !!session.vendorToken,
            hasBaseUrl: !!session.baseUrl,
          });

          setVerificationId(session.verificationId);

          // Validate we received iProov configuration
          if (!session.baseUrl || !session.vendorToken) {
            throw new Error("Invalid iProov configuration from server");
          }

          // Before `ready`, so <iproov-me> never mounts un-upgraded and a failure can't be overwritten.
          await loadIProovWebSdk().catch((sdkErr) => {
            throw new IProovSdkLoadError(sdkErr);
          });
          clearIProovSdkReload();

          // Set iProov configuration - this will trigger the component to render
          setIproovToken(session.vendorToken);
          setIproovBaseUrl(session.baseUrl);
          setState("ready");
        } catch (err) {
          // Likely a 404 mid-deploy; the launcher answers the reloaded frame's IDME_READY.
          if (err instanceof IProovSdkLoadError && reloadOnceForIProovSdk()) {
            logger.warn("[FaceVerification] iProov SDK failed to load; reloading once:", err);
            return;
          }
          logger.error("[FaceVerification] Session creation failed:", err);
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          const statusCode = err instanceof ApiError ? err.statusCode : undefined;
          const responseData = err instanceof ApiError ? err.responseData : undefined;
          const sdkLoadFailed = err instanceof IProovSdkLoadError;
          const errorCode = sdkLoadFailed ? "network_error" : "face_post_api_failed";
          const feedback = sdkLoadFailed ? "iproov_sdk_load_failed" : undefined;

          // Analytics: a caught service error before capture. Any caught error counts — the
          // faces endpoints don't surface statusCode, so we can't gate on a literal 4xx/5xx.
          fileFailureOutcome(feedback ?? errorCode);

          trackFlowError(err, sdkLoadFailed ? 'iproov_sdk_load_failed' : 'session_creation_failed', 'liveness', {
            endpoint: 'POST /v1/verifications/faces',
            sessionTokenPresent: !!token,
            httpStatus: statusCode,
            responseData: responseData ?? null,
          });

          // Use `origin` directly — sendErrorToParent captures a stale parentOrigin (null)
          // at the time this listener was registered, before the state update resolved.
          const allowedOrigin = getAllowedDomains().some(domain => {
            const hostname = new URL(origin).hostname;
            return hostname === domain || hostname.endsWith(`.${domain}`);
          });
          setState("error");
          setError(errorMessage);
          if (allowedOrigin) {
            // Same as above: a direct terminal post, so latch by hand.
            resultReportedRef.current = true;
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_ERROR",
              payload: { errorCode, message: errorMessage, feedback },
            } as VerificationErrorMessage, origin);
          }
        }
      };

      // Register message listener
      window.addEventListener("message", handleInitMessage);

      // Signal to parent that we're ready to receive the token
      // Use origin for security - only the parent who embedded us can receive this
      window.parent.postMessage({
        source: "idme-verification",
        type: "IDME_READY"
      }, origin);

      // Return cleanup function to remove event listener
      return () => {
        window.removeEventListener("message", handleInitMessage);
      };
    } catch (err) {
      logger.error("[FaceVerification] Initialization failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      
      // Track initialization error
      trackFlowError(err, 'initialization_failed', 'liveness', {
        endpoint: 'POST /v1/verifications/faces',
        sessionTokenPresent: !!sessionToken,
      });
      
      handleVerificationError("session_invalid", errorMessage);
      return () => {}; // Return empty cleanup on error
    }
  };

  /**
   * A result the host will SUBMIT has already been handed over, so no second one may be.
   *
   * The host turns a terminal message into a submitted verification result, which
   * increments `live_selfie_attempts`. Post two and the member is charged two attempts for
   * one capture. That used to be self-limiting, because the launcher destroyed the iframe
   * on the first one; now the iframe survives a failure (VEX-7653) and `<iproov-me>` stays
   * mounted on the refused slot, still able to emit, so the invariant needs enforcing here
   * rather than by the teardown.
   *
   * A refusal latches this WITHOUT sending: the host answered `false` because it had
   * already submitted the attempt, so the launch's result is spoken for either way.
   *
   * Note `VERIFICATION_ATTEMPT_FAILED` deliberately does NOT latch it. Each failed capture
   * is a real, separately counted attempt, and the 15s fall-over still has to be able to
   * report a terminal error if the host never answers.
   */
  const resultReportedRef = useRef(false);

  const sendSuccessToParent = useCallback((payload: FaceVerificationSuccessPayload) => {
    if (!parentOrigin) {
      logger.error("[FaceVerification] Cannot send message - parent origin not set");
      return;
    }
    if (resultReportedRef.current) {
      logger.warn("[FaceVerification] Result already reported — dropping duplicate VERIFICATION_COMPLETE");
      return;
    }
    resultReportedRef.current = true;

    const message: VerificationCompleteMessage = {
      source: "idme-verification",
      type: "VERIFICATION_COMPLETE",
      payload,
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  const sendErrorToParent = useCallback((payload: FaceVerificationErrorPayload) => {
    if (!parentOrigin) {
      logger.error("[FaceVerification] Cannot send error - parent origin not set");
      return;
    }
    if (resultReportedRef.current) {
      logger.warn("[FaceVerification] Result already reported — dropping duplicate VERIFICATION_ERROR");
      return;
    }
    resultReportedRef.current = true;

    const message: VerificationErrorMessage = {
      source: "idme-verification",
      type: "VERIFICATION_ERROR",
      payload,
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  const sendStepToParent = useCallback((step: VerificationStep) => {
    if (!parentOrigin) {
      logger.warn("[FaceVerification] Cannot send step - parent origin not set");
      return;
    }

    const message: VerificationStepMessage = {
      source: "idme-verification",
      type: "VERIFICATION_STEP",
      payload: { step },
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  // Enter the camera/motion permission-recovery screen instead of hard-erroring
  // to the parent. Keeps the iframe alive so iProov's permission_denied slot (with
  // its Reload button) stays visible; the user recovers via requestHostReload().
  const enterPermissionBlocked = useCallback((permissionType: BlockedPermission) => {
    logger.warn(`[FaceVerification] ${permissionType} permission denied — showing recovery screen`);
    setBlockedPermission(permissionType);
    trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.CAMERA_PERMISSION_RECOVERY_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION });
    setFlowStage(DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION, "permission_blocked");
    // Recovery renders in the same card as progress.message; denials fire pre-capture
    // so it should be empty already, but clearing is cheap insurance.
    setProgress({ percent: 0, message: "" });
    // Recovery posts no VERIFICATION_ERROR, so this is the only thing keeping the denial
    // observable in IVA (`liveness.step`). On entry, not off the Reload button, so
    // members who abandon here are counted too. (The FaceVerification step effect can't do
    // it — that keys on `state`, and both denials collapse to "permission_blocked".)
    sendStepToParent(permissionType === "motion" ? "device_motion_denied" : "camera_permission_denied");
  }, [sendStepToParent]);

  // Hold on iProov's own `failed` slot rather than posting VERIFICATION_ERROR, which
  // would unmount <iproov-me> and have the launcher destroy the iframe — the reason the
  // slot never rendered and the member only ever saw reasons[0] (VEX-7653).
  const enterCaptureFailed = useCallback(async (feedbacks: string[], message: string) => {
    logger.log("[FaceVerification] capture failed — holding on iProov's failed slot", feedbacks);
    setProgress({ percent: 0, message: "" });
    setRetryAllowed(null);
    captureFailureRef.current = { feedbacks, message };

    // Analytics: one Viewed per failed capture, and NOT an Outcome — the journey isn't
    // over, and only one Outcome may fire per launch. This is what makes the repeat-failure
    // rate measurable now that retries happen inside a single launch instead of each
    // failure ending the iframe. The stage stays `capture`: the member is still on iProov's
    // surface, so a journey that ends here files against the capture section.
    // `reason` rides along because the terminal Outcome carries only the LAST failure's
    // code, which can't answer whether a member failed the same way every time.
    trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.FACE_CAPTURE_FAILURE_SCREEN, "section", {
      section: DOCUMENT_CAPTURE_SECTIONS.CAPTURE,
      reason: feedbacks[0],
    });
    setFlowStage(DOCUMENT_CAPTURE_SECTIONS.CAPTURE, "capture_failed");

    // The GET is the only thing that makes Hydra call iProov's validate (VEX-7795). Awaited
    // before the host is told, because the host's own GET races this one and Hydra has no
    // guard: the loser writes an evaluation-less inspection event that later reads return
    // (VEX-7883). The member is already on the failure card, so only the host waits.
    if (verificationId && sessionToken && !isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
      // Sentry tags API errors by endpoint, which this shares with the success-path poll;
      // without the crumb a dropped validate reads as a failed poll.
      addAPIBreadcrumb(`GET /v1/verifications/faces/${verificationId}`, "validate_trigger", { verificationId });
      try {
        await getVerificationStatus(verificationId, sessionToken);
      } catch (err) {
        logger.warn("[FaceVerification] validate-triggering GET failed after a capture failure", err);
      }
    }

    // Report the attempt WITHOUT the terminal VERIFICATION_ERROR, so the host records it
    // and applies its retry budget while the iframe stays alive on iProov's slot. The
    // host answers with IDME_RETRY_DECISION.
    if (parentOrigin) {
      window.parent.postMessage({
        source: "idme-verification",
        type: "VERIFICATION_ATTEMPT_FAILED",
        payload: {
          errorCode: "face_liveness_failed",
          message,
          feedback: feedbacks[0],
          feedbacks,
          // IVA reads the inspection id from `data.uuid`, as it does on the terminal error.
          data: { uuid: verificationId || "unknown" },
        },
      } as VerificationAttemptFailedMessage, parentOrigin);
    }
  }, [parentOrigin, setFlowStage, verificationId, sessionToken]);


  // Ask the parent SDK to reload the host (top-level) page — the only reliable way
  // to re-prompt camera/motion on iOS Safari, which this nested iframe can't do
  // itself. Triggered by the Reload button on the permission-recovery screen.
  // Mirrors useLegalIdVerification.requestHostReload + useVeriffEvents.onReload
  // (track → flush → delegate) since iProov has no equivalent onReload event.
  const requestHostReload = useCallback(async () => {
    if (!parentOrigin) {
      logger.error("[FaceVerification] Cannot request host reload - parent origin not set");
      return;
    }

    // Analytics: emitted here rather than on the button so it covers every caller. `emit()`
    // uses fetch(keepalive) with no batching, so it survives the host teardown that follows.
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.RETRY_PERMISSION, "button", { section: DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION, targetAction: "navigate" });

    trackIProovReloadRequest(verificationId, blockedPermission ?? "camera");
    // Give the reload metric a chance to send before the host tears us down.
    await flushSentry();

    const message: VerificationReloadMessage = {
      source: "idme-verification",
      type: "VERIFICATION_RELOAD",
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin, verificationId, blockedPermission]);

  const handleVerificationError = useCallback((
    errorCode: FaceVerificationErrorPayload["errorCode"],
    message: string,
    feedback?: string,
    // Accepted, not read: callers (e.g. pollVerificationStatusOnFailure) still pass it through,
    // but the outcome no longer branches on it (VEX-7696) — see trackOutcomeFailed below.
    _context?: VerificationFailureContext,
  ) => {
    // Analytics: the one place a failing journey files its outcome — service fault, permission
    // dead-end, cancel, or a verdict on a completed scan. A judged-and-rejected scan is a
    // failure too (VEX-7696): the reason tells the two apart, the section tells where it ended.
    // A recoverable denial never reaches this function.
    fileFailureOutcome(feedback ?? errorCode);

    setState("error");
    setError(message);
    logger.error(`[FaceVerification] Error [${errorCode}]:`, message, feedback);

    sendErrorToParent({
      errorCode,
      message,
      feedback,
      data: {
        uuid: verificationId || "unknown",
        type: "FACE_VERIFICATION",
        status: "failed",
        outcome: "failed",
        error: message,
      },
    });
  }, [sendErrorToParent, verificationId]);

  /**
   * Retry in place: mint a fresh iProov session and remount, rather than reloading.
   *
   * The `failed` slot's button used to be a bare window.location.reload() — the same
   * blunt approach VEX-7599 had to replace for permission_denied, and worse here since a
   * reload re-inits the whole app when all we need is a new token.
   *
   * Only reachable once the host has answered `allowed: true`; the button stays pending
   * until then, because the retry budget is server-side and this frame cannot see it.
   *
   * <iproov-me> will not start a new claim on an attribute change, so the element is
   * keyed on the token in FaceVerification.tsx — a new token remounts it, and the
   * cancelTransaction teardown fires on the old one.
   */
  const retryCapture = useCallback(async () => {
    if (!sessionToken || retryAllowed !== true) return;

    setRetryAllowed(null);
    setState("initializing");
    try {
      const session = await createVerificationSession({
        sessionToken,
        flow: "face_liveness",
      });
      if (!session.baseUrl || !session.vendorToken) throw new Error("Invalid iProov configuration");
      setVerificationId(session.verificationId);
      setIproovToken(session.vendorToken);
      setIproovBaseUrl(session.baseUrl);
      setState("ready");
    } catch (err) {
      logger.error("[FaceVerification] Retry session creation failed", err);
      handleVerificationError("face_post_api_failed", err instanceof Error ? err.message : "Retry failed");
    }
  }, [sessionToken, retryAllowed, handleVerificationError]);

  /**
   * The host's answer to VERIFICATION_ATTEMPT_FAILED.
   *
   * `false` means the budget is spent and the host is taking over — today IVA reloads
   * into its terminal failure state, which runs the existing trusted-referee escalation
   * unchanged. We deliberately do NOT post VERIFICATION_ERROR here: the host has already
   * submitted this result, and a second post would submit it again and increment the
   * attempt counter twice.
   *
   * No answer at all falls back to the old behaviour — hand to the error path — so a
   * host that never implements this is no worse off than before.
   */
  const applyRetryDecision = useCallback((allowed: boolean) => {
    if (allowed) {
      setRetryAllowed(true);
      return;
    }
    logger.log("[FaceVerification] Retries exhausted — host is taking over");
    setRetryAllowed(false);

    // Analytics: this IS the journey's end — the host takes over from here and this frame
    // will be torn down without ever reaching the error path. Filing here is what keeps a
    // refused attempt from vanishing from the funnel; before this, the terminal
    // VERIFICATION_ERROR carried the Outcome, and not posting it (deliberately, so the
    // host doesn't submit the same result twice) took the Outcome with it. The Outcome is
    // analytics only — no postMessage — so the no-double-submit rule is untouched.
    fileFailureOutcome(captureFailureRef.current?.feedbacks?.[0] ?? "face_liveness_failed");

    // The host refused because it had already submitted this attempt. Latch the result so
    // a late iProov event — the element is still mounted on this slot — cannot post a
    // terminal error and have the same capture submitted, and counted, twice.
    resultReportedRef.current = true;
  }, [fileFailureOutcome]);

  // Its own listener, with a lifetime that matches the session rather than init.
  //
  // This deliberately does NOT ride the IDME_INIT listener: that one is removed the
  // moment IDME_INIT lands (see initializeFaceVerification), which is long before any
  // capture can fail, so a decision handler sharing it would never be reached and the
  // retry button would sit pending until the fall-over timer fired.
  useEffect(() => {
    if (!parentOrigin) return;

    const handleRetryDecision = (event: MessageEvent) => {
      if (event.origin !== parentOrigin) return;
      if (event.data?.type !== "IDME_RETRY_DECISION") return;
      applyRetryDecision(event.data.payload?.allowed === true);
    };

    window.addEventListener("message", handleRetryDecision);
    return () => window.removeEventListener("message", handleRetryDecision);
  }, [parentOrigin, applyRetryDecision]);

  const failOverToHost = useCallback(() => {
    const failure = captureFailureRef.current;
    logger.warn("[FaceVerification] No retry decision from host — falling back to the error path");
    handleVerificationError(
      "face_liveness_failed",
      failure?.message ?? "Verification failed",
      failure?.feedbacks?.[0]
    );
  }, [handleVerificationError]);

  const pollVerificationStatusOnFailure = useCallback(async (
    errorCode: FaceVerificationErrorPayload["errorCode"],
    message: string,
    feedback?: string,
    context?: VerificationFailureContext,
  ) => {
    // Analytics: a completed scan hands off to the backend, so the journey reaches
    // `processing` exactly as a clean one does — same screen, same stage. Both the view
    // and the section move, which is what makes the two paths comparable in the funnel.
    //
    // Gated on a completed scan because `setState` is not: a cancel or an unsupported
    // device passes through this state for a few hundred ms on its way to the error
    // screen, which is no screen anyone saw and no stage anything reached.
    if (context?.scanCompleted) {
      trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.PROCESSING_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.PROCESSING });
      setFlowStage(DOCUMENT_CAPTURE_SECTIONS.PROCESSING, "processing");
    } else {
      setState("processing");
    }

    if (verificationId && sessionToken && !isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
      try {
        await getVerificationStatus(verificationId, sessionToken);
      } catch {
        // GET failed - still report iProov error to parent
      }
    }

    handleVerificationError(errorCode, message, feedback, context);
  }, [verificationId, sessionToken, handleVerificationError]);

  const pollVerificationStatus = useCallback(async () => {
    if (!verificationId || !sessionToken) {
      handleVerificationError("network_error", "Missing verification ID or session token");
      return;
    }

    setState("processing");

    try {
      logger.log("[FaceVerification] Polling verification status...");

      addAPIBreadcrumb(`GET /v1/verifications/faces/${verificationId}`, 'polling_start', { maxAttempts: API_CONFIG.MAX_POLL_ATTEMPTS });

      // Add retry logic for polling (backend may need time to process)
      let attempts = 0;
      const maxAttempts = API_CONFIG.MAX_POLL_ATTEMPTS;
      const pollInterval = API_CONFIG.POLL_INTERVAL_MS;

      while (attempts < maxAttempts) {
        // Guard: abort if the token won't last another 2 minutes
        if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
          logger.warn("[FaceVerification] Token expiring soon, aborting poll");
          handleVerificationError("token_expired", "Session token has expired");
          return;
        }

        try {
          const result = await getVerificationStatus(verificationId, sessionToken);
          logger.log("[FaceVerification] Verification status:", result);

          addAPIBreadcrumb(`GET /v1/verifications/faces/${verificationId}`, 200, {
            attempts: attempts + 1,
            status: result?.status,
            outcome: result?.outcome,
          });

          // Analytics: the journey ends here — the backend answered. A terminal rejection is a
          // failure, not a pass (VEX-7696), and `reason` carries the backend's verdict verbatim.
          // The member-facing flow below is unaffected either way.
          const backendOutcome = result?.outcome ?? "";
          if (BACKEND_REJECTION_OUTCOMES.has(backendOutcome)) {
            fileFailureOutcome(backendOutcome);
          } else {
            trackOutcomePassed(flowSectionRef.current);
          }

          setState("completed");

          // Send API response to parent window (wrapped in data object for SDK interface)
          // Include fallback response if API didn't return data
          sendSuccessToParent({
            data: result || {
              uuid: verificationId,
              type: "FACE_VERIFICATION",
              status: "completed",
              outcome: "unknown",
              results: [],
            },
          });

          return;
        } catch (err) {
          const statusCode = err instanceof ApiError ? err.statusCode : undefined;
          // 401 — fail immediately, don't retry
          if (statusCode === 401) {
            throw err;
          }
          attempts++;
          logger.log(`[FaceVerification] Polling attempt ${attempts}/${maxAttempts} failed`);

          if (attempts >= maxAttempts) {
            throw err;
          }
          // Wait before next attempt
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }
      }

      throw new Error("Failed to get verification status after multiple attempts");
    } catch (err) {
      logger.error("[FaceVerification] Failed to get verification status:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to verify status";

      // Track polling failure
      trackFlowError(err, 'polling_failed', 'liveness', {
        endpoint: `GET /v1/verifications/faces/${verificationId}`,
        verificationId: verificationId || 'unknown',
        sessionTokenPresent: !!sessionToken,
      });

      sendErrorToParent({
        errorCode: "face_get_api_failed",
        message: errorMessage,
        data: {
          uuid: verificationId || "unknown",
          type: "FACE_VERIFICATION",
          status: "failed",
          outcome: "failed",
          results: [],
          error: errorMessage,
        },
      });

      handleVerificationError("face_get_api_failed", errorMessage);
    }
  }, [verificationId, sessionToken, sendSuccessToParent, sendErrorToParent, handleVerificationError]);

  return {
    // State
    state,
    error,
    verificationId,
    sessionToken,
    iproovToken,
    iproovBaseUrl,
    progress,
    consentEnabled,
    blockedPermission,

    // State setters
    setState,
    setFlowStage,
    flowSectionRef,
    setProgress,

    // Methods
    initializeFaceVerification,
    pollVerificationStatus,
    pollVerificationStatusOnFailure,
    handleVerificationError,
    enterPermissionBlocked,
    enterCaptureFailed,
    retryCapture,
    retryAllowed,
    applyRetryDecision,
    failOverToHost,
    requestHostReload,
    sendStepToParent,
    isSessionTokenExpiringSoon: useCallback(
      () => isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined),
      []
    ),
  };
}
